import React, { useContext, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import { type ReactLocalization, useLocalization } from "@fluent/react";
import { Row, ResponsiveFlex, Button } from "@narmi/design_system";
import { entities, dollars } from "byzantine";
import { useNotificationContext } from "cerulean";
import Account from "byzantine/src/Account";
import Filters from "byzantine/src/filters";
import {
  BulkPaymentRowType,
  calculateBulkTotals,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useCreateBulkPaymentFromTemplate } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import Container from "../../Container";
import HeaderWithSteps from "../../HeaderWithSteps";
import styles from "../../transfer/ach/ReviewScreen.module.scss";
import AccountContext from "../../contexts/AccountContext";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { useFingerPrintData } from "../../fingerprint";
import ReviewPaymentDialog from "./ReviewPaymentDialog";
import { formatPaymentTotal } from "./templates/templateHelpers";
import { useTemplateContext } from "./templates/TemplateContext";
import { useACHImportContext } from "../ACHImportContext";
import useShowCollectAmounts from "./useShowCollectAmounts";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";

export type RowType = [string, string | React.ReactElement<any> | undefined];
interface RowWrapperType {
  heading?: string;
  content?: string | React.ReactElement<any>;
}

export const RowWrapper = ({ heading, content }: RowWrapperType) => {
  if (!content) return null;

  return (
    <Row alignItems="top">
      <Row.Item shrink>
        <p className="fontColor--secondary">{heading}</p>
      </Row.Item>
      <Row.Item>
        <div className={styles.reviewRowContent}>
          <span className={styles.reviewRowValue}>{content}</span>
        </div>
      </Row.Item>
    </Row>
  );
};

export const getSendingCollectingRows = (
  l10n: ReactLocalization,
  {
    showCollectAmounts,
    sendTotalFormatted,
    collectTotalFormatted,
    totalSendPayments,
    totalCollectPayments,
    totalPayments,
  }: {
    showCollectAmounts: boolean;
    sendTotalFormatted: string;
    collectTotalFormatted: string;
    totalSendPayments: number;
    totalCollectPayments: number;
    totalPayments: number;
  }
): RowType[] => {
  const rows: RowType[] = [];

  if (showCollectAmounts) {
    if (totalSendPayments > 0) {
      rows.push([
        l10n.getString("label-sending"),
        formatPaymentTotal(l10n, sendTotalFormatted, totalSendPayments),
      ]);
    }
    if (totalCollectPayments > 0) {
      rows.push([
        l10n.getString("label-collecting"),
        formatPaymentTotal(l10n, collectTotalFormatted, totalCollectPayments),
      ]);
    }
  } else {
    rows.push([
      l10n.getString("label-sending"),
      formatPaymentTotal(l10n, sendTotalFormatted, totalPayments),
    ]);
  }

  return rows;
};

export const getScheduledDateRange = (payments: BulkPaymentRowType[]) => {
  const dates = payments.map((p) =>
    DateTime.fromFormat(p.scheduledDate, "MM/dd/yyyy")
  );

  const minDate = DateTime.min(...dates);
  const maxDate = DateTime.max(...dates);

  if (!minDate || !maxDate) return "";

  if (minDate?.hasSame(maxDate, "day")) {
    return minDate.toFormat("MM/dd/yyyy");
  }

  return `${minDate?.toFormat("MM/dd/yyyy")} - ${maxDate.toFormat(
    "MM/dd/yyyy"
  )}`;
};

export const ReviewBulkScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const {
    expedited_payments: expeditedPaymentsFeature,
    ach_templates: achTemplatesFeature,
  } = useUserFeatures();
  const { accounts } = useContext(AccountContext);
  const { sendNotification } = useNotificationContext();
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const { send: fetchACHCompanies, loading: achCompaniesLoading } =
    entities.achCompanies.useFetchACHCompanies();
  const achCompany = entities.achCompanies.useACHCompany();
  const { createBulkPayment } = entities.bulkPayments.useCreateBulkPayment();
  const { createBulkPaymentFromTemplate } = useCreateBulkPaymentFromTemplate();
  const { deviceId, deviceRequestId } = useFingerPrintData("bulk_payment");
  const {
    isTemplateMode,
    template,
    loading: templateLoading,
    excludedRows,
    templateQueryString,
  } = useTemplateContext();
  const { organizationFileUuid, importType } = useACHImportContext();
  const isImportMode = importType !== null;

  const [reviewPaymentDialogIsOpen, setReviewPaymentDialogIsOpen] =
    useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { values } = form;
  const {
    title,
    fromAccount,
    secCode,
    entryDesc,
    groupPosting,
    payments: allPayments,
    description,
    totalExpediteFees,
  } = values;
  // in template mode, show only included payments on the review screen
  const payments = useMemo(
    () =>
      isTemplateMode
        ? allPayments.filter((_, i) => !excludedRows.has(i))
        : allPayments,
    [allPayments, isTemplateMode, excludedRows]
  );
  const {
    sendTotalFormatted,
    collectTotalFormatted,
    netTotalFormattedWithSign,
  } = calculateBulkTotals(payments);
  const totalPayments = payments.length;
  const totalSendPayments = payments.filter(
    (p) => p.transactionType === "send"
  ).length;
  const totalCollectPayments = payments.filter(
    (p) => p.transactionType === "collect"
  ).length;
  const showCollectAmounts = useShowCollectAmounts(totalCollectPayments > 0);
  const senderAccountData = accounts.find(
    (account: Account) => account.id === fromAccount
  );
  const senderAccount = new Account(senderAccountData || {});
  const scheduledDateRange = getScheduledDateRange(payments);

  useEffect(() => {
    fetchACHCompanies(`institution_account=${senderAccountData?.id}`);
  }, [senderAccountData, fetchACHCompanies]);

  const handleGoBack = () => {
    navigate(`../payments${templateQueryString}`);
  };
  const handleError = (screenWithError: {
    screen: number;
    message: string;
    field?: string;
    paymentIndex?: number;
  }) => {
    const { screen, message, field, paymentIndex } = screenWithError;
    if (screen === 1) {
      if (field) {
        form.setFieldError(field, message);
        form.setFieldTouched(field, true, false);
      }

      if (isImportMode) {
        // In the ACH file import flow, the first-screen fields (title, account,
        // SEC code, entry description) live on the import "additional details"
        // screen rather than the manual `bulk/new` screen. A duplicate title is
        // the expected error here, so route the user back to that screen with an
        // inline error on the title field instead of dropping them into the
        // manual flow (which discarded the import). See PRO-1633.
        navigate(`/${PAYMENT_ACTIVITY_ROUTE}/upload/additional-details`);
      } else {
        // Keep all entered payment data in the shared form and send the user
        // back to the first manual step with the title error surfaced inline.
        navigate(
          achTemplatesFeature
            ? `../details${templateQueryString}`
            : `..${templateQueryString}`
        );
      }
    } else if (field && paymentIndex !== undefined) {
      const isExpedite = field === "chosenExpediteOption";
      const rowPath = `payments.${paymentIndex}`;
      navigate(`../payments${templateQueryString}`, {
        state: {
          rowError: {
            path: `${rowPath}.${isExpedite ? "expediteChoice" : field}`,
            valuePath: isExpedite ? field : `${rowPath}.${field}`,
            message,
          },
        },
      });
    } else {
      handleGoBack();
    }
    sendNotification({
      type: "negative",
      text: message,
    });
  };

  const idempotencyKey = useMemo(() => uuidv4() as UUID, []);

  const templateNotReady = isTemplateMode && !template?.activeVersionId;

  const handleClickSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const onSuccess = () => {
      navigate(`../receipt${templateQueryString}`);
    };

    const onError = (screenWithError: Parameters<typeof handleError>[0]) => {
      setIsSubmitting(false);
      handleError(screenWithError);
    };

    if (isTemplateMode) {
      if (!template?.activeVersionId) {
        setIsSubmitting(false);
        return;
      }
      createBulkPaymentFromTemplate(
        idempotencyKey,
        { template, excludedRows },
        { onSuccess, onError },
        deviceId,
        deviceRequestId
      );
    } else {
      await form.setFieldValue("payments", payments);
      createBulkPayment(
        idempotencyKey,
        { onSuccess, onError },
        deviceId,
        deviceRequestId,
        organizationFileUuid ?? undefined
      );
    }
  };

  const toggleReviewDialog = () => {
    setReviewPaymentDialogIsOpen(!reviewPaymentDialogIsOpen);
  };

  const renderRow = (row: RowType, index: number) => {
    const [heading, content] = row;

    return (
      <RowWrapper
        key={`row-${heading}__${index}`}
        heading={heading}
        content={content}
      />
    );
  };

  const topRows: RowType[] = getSendingCollectingRows(l10n, {
    showCollectAmounts,
    sendTotalFormatted,
    collectTotalFormatted,
    totalSendPayments,
    totalCollectPayments,
    totalPayments,
  });

  if (expeditedPaymentsFeature && totalExpediteFees && totalExpediteFees > 0) {
    topRows.push([
      l10n.getString("transaction-detail-fees"),
      Filters.currency(dollars(totalExpediteFees as number), {
        hasDecimal: true,
      }),
    ]);
  }

  topRows.push([
    l10n.getString("label-bulk-details"),
    <Button
      kind="plain"
      size="m"
      onClick={toggleReviewDialog}
      testId="toggle-review-dialog"
    >
      {l10n.getString("view-payments-cta", {
        total: totalPayments,
      })}
      <span className="narmi-icon-chevron-right" />
    </Button>,
  ]);

  const paymentRows: RowType[] = [];

  if (isTemplateMode && template) {
    paymentRows.push(
      [l10n.getString("label-wire-template--template-used"), template.name],
      [
        l10n.getString("label-version"),
        template.versionNumber != null
          ? `V${template.versionNumber}`
          : undefined,
      ]
    );
  }

  paymentRows.push(
    [l10n.getString("label-payment-title"), title],
    [l10n.getString("label-dates"), scheduledDateRange],
    [l10n.getString("label-account"), senderAccount.getShortDescription()],
    [l10n.getString("label-payment-type"), secCode],
    [l10n.getString("label-purpose-payment"), entryDesc]
  );

  if (description) {
    paymentRows.push([
      l10n.getString("label-international-wire-review-note"),
      description,
    ]);
  }

  if (achCompany?.company_id) {
    paymentRows.push([
      l10n.getString("ach-payment-review-company"),
      `${achCompany?.company_name} / ${achCompany?.company_id}`,
    ]);
  }

  return (
    <Container>
      <ResponsiveFlex gapSize="xl">
        <HeaderWithSteps
          useDisplayFont
          headerText={l10n.getString("review-your-payment-heading")}
        />

        <div className={styles.reviewCardContainer}>
          <div className={styles.reviewCardHeader}>
            <h1>
              {showCollectAmounts
                ? netTotalFormattedWithSign
                : `-${sendTotalFormatted}`}
            </h1>
          </div>

          <div className={styles.reviewCardSection}>
            <ResponsiveFlex gapSize="m">
              {topRows.map((row, index) => renderRow(row, index))}
            </ResponsiveFlex>
            <div className="fontSize--s fontColor--primary margin--top--xs">
              {groupPosting
                ? l10n.getString("group-posting-copy")
                : l10n.getString("review-posting-single-copy")}
            </div>
          </div>

          <div className={styles.reviewCardSectionBordered}>
            <ResponsiveFlex gapSize="m">
              <h5 className="fontSize--xs fontWeight--bold">
                {" "}
                {l10n.getString("ach-payment-payment-details").toUpperCase()}
              </h5>
              {paymentRows.map((row, index) => renderRow(row, index))}
            </ResponsiveFlex>
          </div>
        </div>

        <div className="margin--top--xl">
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                kind="negative"
                label={l10n.getString("button-back")}
                onClick={handleGoBack}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                label={l10n.getString("button-submit")}
                onClick={handleClickSubmit}
                disabled={
                  templateNotReady ||
                  templateLoading ||
                  achCompaniesLoading ||
                  isSubmitting
                }
              />
            </Row.Item>
          </Row>
        </div>
      </ResponsiveFlex>
      <ReviewPaymentDialog
        isOpen={reviewPaymentDialogIsOpen}
        handleClose={toggleReviewDialog}
        payments={payments}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
      />
    </Container>
  );
};

export default ReviewBulkScreen;
